// AI Generated Test
import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { LinksRepoService } from '@client/app/services/api/links-repo/links-repo.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { SessionService } from '@monsido/core/session/session.service';
import { EditAltTextService } from '@client/app/modules/page-fix/services/form/edit-alt-text/edit-alt-text.service';
import { PageFixRepoService } from '@client/app/services/api/pagefix-repo/page-fix-repo.service';
import { PAGE_FIX_CHANGE_TYPES } from '@client/app/modules/page-fix/constants/page-fix-change-types';
import { PAGE_FIX_FIXABLE_TYPES } from '@client/app/modules/page-fix/constants/page-fix-fixable-types';
import { PAGE_FIX_SELECTOR_QUERIES } from '@client/app/modules/page-fix/constants/page-fix-selector-queries';
import { PageFixModelHelper } from '../../services/model-helper/page-fix-model-helper';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';

@Component({
    selector: 'page-fix-form-edit-alt-text',
    templateUrl: './page-fix-form-edit-alt-text.component.html',
    standalone: false,
})
export class PageFixFormEditAltTextComponent implements OnInit {
    @Input() resolve: any;
    @Output() close = new EventEmitter<any>();
    @Output() dismiss = new EventEmitter<void>();

    type: any;
    model: any;
    pageFix: any;
    checkId: number = 0;
    fixableId: number = 0;
    imgThumbnail: string | null = null;
    informationText: string = '';
    altText = '';
    loading = true;
    urls: string[] = [];
    pageFixSelector = '';

    constructor (
        private translateService: TranslateService,
        private linksRepoService: LinksRepoService,
        private monEventService: MonEventService,
        private sessionService: SessionService,
        private editAltTextService: EditAltTextService,
        private pageFixRepoService: PageFixRepoService,
        private monModalService: MonModalService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    activate (): void {
        this.type = this.resolve.type;
        this.model = this.resolve.model;
        this.pageFix = this.resolve.pageFix;
        this.checkId = this.pageFix ? this.pageFix.change.check_id : this.model.id;
        this.fixableId = this.resolve.typeId || this.checkId;
        this.imgThumbnail = null;
        this.informationText = this.getTextDescription(this.checkId);
        this.altText = '';
        this.loading = true;
        this.urls = [];
        this.pageFixSelector = '';

        if (!this.pageFix) {
            this.getPageLinks()
                .then((links: any[]) => {
                    if (links.length === 0) {
                        this.sendError();
                        return;
                    }
                    return this.getOriginalUrls(links[0].link_id).then(
                        (urls: any[]) => {
                            this.pageFixSelector = this.getPageFixSelector(urls);
                            this.urls = urls.map((url: any) => url.url);
                            this.imgThumbnail = this.getImageURL();
                        },
                        () => {
                            this.pageFixSelector = PageFixModelHelper.getSelectorValue(this.model.source_code, ['src']);
                        },
                    );
                })
                .catch(() => {})
                .finally(() => {
                    this.loading = false;
                });
        } else {
            this.loading = false;
            this.urls = this.getPageFixUrls(this.pageFix);
            this.imgThumbnail = this.getImageURL();
            this.altText = this.pageFix.change.value;
        }
    }

    getTranslationText (): string {
        return this.translateService.getString('', {});
    }

    getImageURL (): string {
        if (this.urls && this.urls.length > 0) {

            if (/[a-zA-Z\/0-9]*:\/\//.test(this.urls[0])) {
                return this.urls[0];
            } else {
                return (this.sessionService.domain?.url || '').replace(/\/$/, '') + '/' + this.urls[0].replace(/^\//, '');
            }
        }
        return '';
    }

    getUrlOfSnippet (): string | false {
        this.editAltTextService.setHTMLSnippet(this.model.html_snippet);
        if (this.editAltTextService.canReadSource()) {
            return this.editAltTextService.getSource();
        }
        this.sendError();
        this.monModalService.alert(
            this.translateService.getString(
                'Something went wrong. The Acquia Web Governance Team has been notified of this error and will get back to you, as soon as possible.',
            ),
        );
        return false;
    }

    getPageLinks (): Promise<any[]> {
        const params = {
            search: this.getUrlOfSnippet() || '',
            page: 1,
            page_size: 1,
        };
        if (params.search !== '') {
            return this.linksRepoService.getAllPageLinks(params);
        } else {
            return Promise.resolve([]);
        }
    }

    getOriginalUrls (id: number): Promise<any[]> {
        const link = {
            id: id,
        };
        return this.linksRepoService.getOriginalUrls(link);
    }

    sendError (): void {
        this.monEventService.run(
            'newException',
            'PageCorrect component \'pageFixFormEditAltText\' errored on snippet "' + this.model.html_snippet + '"',
            'error',
        );
    }

    submit (): void {
        let promise;
        const payload: any = {
            change: {
                check_id: this.checkId,
                attribute: 'alt',
                value: this.altText,
            },
        };

        if (!this.pageFix) {
            payload.selector = this.pageFixSelector;
            payload.fixable_type = PAGE_FIX_FIXABLE_TYPES.accessibility_source_code;
            payload.fixable_id = this.fixableId;
            payload.change_type = PAGE_FIX_CHANGE_TYPES.attribute + ':alt';
        }

        if (this.pageFix) {
            promise = this.pageFixRepoService.update(this.pageFix, payload, {});
        } else {
            promise = this.pageFixRepoService.create(payload, contextTokens.NO_GLOBAL);
        }

        promise.then(
            (result: any) => {
                this.closeModal(result);
            },
            (res: any) => {
                if (res.status === 422 && res.data.message === 'Record not unique') {
                    this.monModalService
                        .alert(this.translateService.getString('Someone have created this PageCorrect before you, on another check.'))
                        .then(() => {}, () => {});

                } else {
                    this.monModalService
                        .alert(this.translateService.getString('PageCorrect failed to save, the Acquia Web Governance team has been notified of the error'))
                        .then(() => {}, () => {});
                    this.monEventService.run(
                        'newException',
                        'PageCorrect component \'pageFixFormEditAltText\' was unable to save"',
                        'error',
                        { payload: payload },
                    );
                }
            },
        );
    }

    closeModal (pageFix: any): void {
        this.close.emit({ $value: pageFix });
    }

    getTextDescription (checkId: number): string {
        switch (checkId) {
            case 1:
                return this.translateService.getString('All <code>img</code> elements have an <code>alt</code> attribute');
            case 2:
                return this.translateService.getString(
                    'Alt text is not the same as the filename unless author has confirmed it is correct',
                );
            case 3:
                return this.translateService.getString('Non-Decorative images must have Alt text');
            case 4:
                return this.translateService.getString('Image Alt text is short');
            case 5:
                return this.translateService.getString('Important images should not have spacer Alt text');
            case 6:
                return this.translateService.getString(
                    'Alt text for all \u003ccode\u003eimg\u003c/code\u003e elements is not placeholder text unless author has confirmed it is correct',
                );
            case 7:
                return this.translateService.getString(
                    'Alt text for all \u003ccode\u003eimg\u003c/code\u003e elements used as source anchors is not empty when there is no other text in the anchor',
                );
            case 11:
                return this.translateService.getString(
                    'Alt text for all \u003ccode\u003eimg\u003c/code\u003e elements contains all text in the image unless the image text is decorative or appears elsewhere in the document',
                );
            case 16:
                return this.translateService.getString(
                    'Alt text for all <code>img</code> elements is the empty string ("") if the image is decorative.',
                );
            case 178:
                return this.translateService.getString('Alt text does not convey the same information as the image');
            default:
                return '';
        }
    }

    getPageFixSelector (urls: any[]): string {
        const query = urls.map((url: any) => {
            return 'img[src="' + url.url + '"]';
        });
        return PAGE_FIX_SELECTOR_QUERIES.query_selector + ';' + JSON.stringify(query);
    }

    getPageFixUrls (pageFix: any): string[] {
        const selectorQueries = pageFix.selector
            .split(';')
            .slice(1)
            .join(';');
        return JSON.parse(selectorQueries).map((selector: string) => {
            const match = selector.match(/.*="(.*)"]/);
            return match ? match[1] : '';
        });
    }

    cancel (): void {
        this.dismiss.emit();
    }
}
